// §4 date primitive: a table date that carries urgency. Overdue → danger,
// within 7 days → warning, otherwise default. Shows the relative form beside the
// absolute ("Aug 4 · in 7d"). A `done` date (a closed/resolved record) keeps the
// neutral default — a past deadline on a closed item is history, not exposure.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatLocalYmd, formatUtcYmd, parseLocalDate, parseUtcDate } from '../api/nullable';
const DAY_MS = 86400000;
function relativeLabel(days) {
    if (days === 0) return 'today';
    if (days > 0) return `in ${days}d`;
    return `${-days}d ago`;
}
// A UTC-anchored value (e.g. deliverable due dates, materialized at UTC
// midnight) must have its day count computed against a UTC "today" too —
// comparing it to a LOCAL midnight "today" is the same class of bug as
// reading the calendar day with local getters, just moved into the math.
function daysUntil(date, anchor) {
    if (anchor === 'utc') {
        return Math.floor(date.getTime() / DAY_MS) - Math.floor(Date.now() / DAY_MS);
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((date.getTime() - today.getTime()) / DAY_MS);
}
export function UrgencyDate({ value, done = false, anchor = 'local' }) {
    const ymd = anchor === 'utc' ? formatUtcYmd(value) : formatLocalYmd(value);
    if (!ymd) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    const date = anchor === 'utc' ? parseUtcDate(ymd) : parseLocalDate(ymd);
    const days = daysUntil(date, anchor);
    const abs = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...anchor === 'utc' ? {
            timeZone: 'UTC'
        } : {}
    });
    const rel = relativeLabel(days);
    // Urgency tone only for live records; a done record stays neutral.
    const tone = done ? 'text-foreground' : days < 0 ? 'text-danger' : days <= 7 ? 'text-warning' : 'text-foreground';
    return /*#__PURE__*/ _jsxs("span", {
        className: tone,
        children: [
            abs,
            " ",
            /*#__PURE__*/ _jsxs("span", {
                className: "opacity-70",
                children: [
                    "· ",
                    rel
                ]
            })
        ]
    });
}
